<script setup>
import { i18n } from 'boot/i18n';
import {
  HrErrorBanner,
  HrFilterChipGroup,
  HrIconDestructiveBtn,
  HrListSelect,
  HrStatusActionChip,
  HrSubtleBtn,
  HrTextField,
} from 'src/design-system';
import { computed, ref } from 'vue';

import {
  CAP_PERIOD,
  STATED_UNIT,
  isPeriodicCap,
} from '../../domain/constants/authorization';

/**
 * One approved limit: the services drawing on it, its ceilings, and the settings it is scoped to.
 *
 * THE CAP READS AS A SENTENCE — "Approved [1,040] [units] [for the auth period]" — because the
 * biller is transcribing, not configuring. Three labeled fields would make them map the paper onto
 * our vocabulary first.
 *
 * THE UI OFFERS TWO CAPS BUT THE MODEL HAS NO MAXIMUM: a letter stating a window, a weekly and a
 * daily ceiling is rare but real.
 *
 * NO RESET-BEHAVIOR CONTROL, by product decision, so the field is written null on every cap and
 * consumption must hold rather than assume. Do not add a default here to fill the gap — it would
 * later read as a decision somebody made.
 *
 * "ANY SETTING" IS THE EMPTY SELECTION and is not the same statement as selecting every code listed,
 * because a code added next year is covered by the first and not the second.
 */
defineOptions({ name: 'ApprovedLimitEditor' });

const props = defineProps({
  /** The limit draft being edited: `{ limitId, services, placeOfServiceCodes, caps }`. */
  modelValue: {
    type: Object,
    required: true,
  },
  /** Distinct place-of-service codes the clinic uses, as `{ label, value }`. */
  settingOptions: {
    type: Array,
    default: () => [],
  },
  /**
   * The clinic's settings could not be READ, as opposed to there being none. Both leave the picker
   * empty and both leave the limit covering any setting, but only one of them is a fault the biller
   * can do something about.
   */
  settingsUnavailable: {
    type: Boolean,
    default: false,
  },
  /** Validation codes for this limit, as `{ code, capIndex }`. */
  errors: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const unitOptions = Object.values(STATED_UNIT).map((value) => ({
  value,
  label: i18n.t(`billingAuthorizations.statedUnit.${value}`),
}));

const periodOptions = Object.values(CAP_PERIOD).map((value) => ({
  value,
  label: i18n.t(`billingAuthorizations.capPeriod.${value}`),
}));

const isPickingSettings = ref(false);

/**
 * Derived from the model rather than from the picker being open, so the two cannot drift. A COUNT
 * rather than the names, which are already on the chips below and grow without bound.
 */
const settingsSummary = computed(() => {
  const { length } = props.modelValue.placeOfServiceCodes;

  return length === 0
    ? i18n.t('billingAuthorizations.anyPlaceOfService')
    : i18n.tc('billingAuthorizations.placeOfServiceCount', length, { count: length });
});

/** Which cap periods are already spoken for, so a second cap cannot repeat one. */
const takenPeriods = computed(() => new Set(props.modelValue.caps.map((cap) => cap.period)));

const periodOptionsFor = (capIndex) => periodOptions.map((option) => ({
  ...option,
  disable: takenPeriods.value.has(option.value)
    && props.modelValue.caps[capIndex]?.period !== option.value,
}));

const errorFor = (capIndex, code) => {
  return props.errors.some((error) => error.capIndex === capIndex && error.code === code);
};

/**
 * The failures that name the LIMIT rather than one of its caps, so no field on this form can carry
 * them. The service picker refuses an empty selection and the period options disable one already
 * taken, which rules every one of them out here and leaves a stored letter as the only source —
 * where, with nowhere to sit, they disabled Save and said nothing.
 */
const limitMessages = computed(() => props.errors
  .filter((error) => error.capIndex === null)
  .map((error) => i18n.t(`billingAuthorizations.errors.${error.code}`)));

/**
 * One rule per located code, so the reason sits under the field rather than coloring it red.
 * Each rule reads the current errors for the zero-based cap index and returns the translated
 * message when its code is present, or true otherwise; it does not validate the field value.
 */
const capRules = (capIndex, ...codes) => codes.map(
  (code) => () => !errorFor(capIndex, code) || i18n.t(`billingAuthorizations.errors.${code}`),
);

/**
 * A cleared or unreadable field is UNSTATED, not zero. `Number('')` is 0, and 0 is a denial rather
 * than an approval — it would validate as a required-figure error while the row echoed "Approved 0
 * units", which reads as something the funder said.
 */
const toFigure = (value) => {
  const figure = Number(value);

  return value === '' || value === null || !Number.isFinite(figure) ? null : figure;
};

const patch = (changes) => emit('update:modelValue', { ...props.modelValue, ...changes });

const patchCap = (capIndex, changes) => {
  patch({
    caps: props.modelValue.caps.map((cap, index) => {
      if (index !== capIndex) {
        return cap;
      }
      const next = { ...cap, ...changes };

      // Inapplicable off a periodic cap, so switching to the whole authorization drops it rather
      // than leaving a value the model would silently discard on save.
      return isPeriodicCap(next.period) ? next : { ...next, resetBehavior: null };
    }),
  });
};

const addCap = () => {
  patch({
    caps: [...props.modelValue.caps, {
      figure: null, statedUnit: null, period: null, resetBehavior: null,
    }],
  });
};

const removeCap = (capIndex) => {
  patch({ caps: props.modelValue.caps.filter((_cap, index) => index !== capIndex) });
};
</script>

<template>
  <div class="row q-col-gutter-md">
    <div
      v-if="limitMessages.length > 0"
      class="col-12"
    >
      <div class="row q-col-gutter-sm">
        <div
          v-for="message in limitMessages"
          :key="message"
          class="col-12"
        >
          <hr-error-banner
            :message="message"
            data-e2e="banner-authorization-limit-error"
          />
        </div>
      </div>
    </div>

    <!-- THE CAPS AND THE ACTION THAT ADDS ONE ARE A GROUP, and the tighter gutter inside is what
         says so. The wider gutter outside separates them from the setting, which scopes the whole
         limit rather than belonging to any one cap. -->
    <div class="col-12">
      <div class="row q-col-gutter-sm">
        <!-- The fields carry aria-labels, so the sentence is only a visual arrangement. The lead-in
             takes a column of its own so both caps' fields start at the same place — the words are
             different lengths and the sentence only reads as one if the rows line up. -->
        <div
          v-for="(cap, capIndex) in modelValue.caps"
          :key="capIndex"
          class="col-12"
        >
          <div class="row items-start q-col-gutter-sm">
            <div class="col-12 col-sm-2">
              <span class="approved-limit-editor__lead approved-limit-editor__beside-field">
                {{ capIndex === 0
                  ? i18n.t('billingAuthorizations.capLeadIn')
                  : i18n.t('billingAuthorizations.capLeadInSecond') }}
              </span>
            </div>
            <hr-text-field
              class="col-12 col-sm-2"
              :model-value="cap.figure"
              type="number"
              min="0"
              :aria-label="i18n.t('billingAuthorizations.fields.figure')"
              :placeholder="i18n.t('billingAuthorizations.fields.figure')"
              :rules="capRules(capIndex, 'capFigureRequired', 'capFigureNotPositive')"
              hide-bottom-space
              data-e2e="input-authorization-cap-figure"
              @update:model-value="patchCap(capIndex, { figure: toFigure($event) })"
            />
            <hr-list-select
              class="col-12 col-sm-3"
              :model-value="cap.statedUnit"
              :options="unitOptions"
              :aria-label="i18n.t('billingAuthorizations.fields.statedUnit')"
              :rules="capRules(capIndex, 'capStatedUnitInvalid')"
              hide-bottom-space
              data-e2e="select-authorization-cap-unit"
              @update:model-value="patchCap(capIndex, { statedUnit: $event })"
            />
            <hr-list-select
              class="col-12 col-sm-4"
              :model-value="cap.period"
              :options="periodOptionsFor(capIndex)"
              :aria-label="i18n.t('billingAuthorizations.fields.capPeriod')"
              :rules="capRules(capIndex, 'capPeriodInvalid')"
              hide-bottom-space
              data-e2e="select-authorization-cap-period"
              @update:model-value="patchCap(capIndex, { period: $event })"
            />
            <!-- The first cap has no remove control: a service must state at least one figure. -->
            <div
              v-if="capIndex > 0"
              class="col-12 col-sm-1"
            >
              <div class="approved-limit-editor__beside-field">
                <hr-icon-destructive-btn
                  icon="close"
                  :title="i18n.t('billingAuthorizations.actionRemoveCap')"
                  data-e2e="btn-remove-authorization-cap"
                  @click="removeCap(capIndex)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- OFFSET TO THE FIGURE COLUMN so it reads as the continuation of the cap rows and takes
             the position the second cap will. Low emphasis and no border, because it is not a
             sibling of the setting chip below — that one scopes the whole limit, while most letters
             state a single cap and never reach this. -->
        <div
          v-if="modelValue.caps.length < 2"
          class="col-12 col-sm-10 offset-sm-2"
        >
          <hr-subtle-btn
            :label="i18n.t('billingAuthorizations.actionAddCap')"
            icon="add"
            data-e2e="btn-add-authorization-cap"
            @click="addCap"
          />
        </div>
      </div>
    </div>

    <!-- THE LABEL CARRIES THE STATE, THE CHEVRON THE DISCLOSURE. A "restrict" button implied a mode
         you could be in with nothing selected, which the model cannot represent — an empty list IS
         any setting. Collapsed by default, since that is the answer on almost every letter. -->
    <div class="col-12">
      <hr-status-action-chip
        v-model="isPickingSettings"
        :label="settingsSummary"
        icon="location_on"
        chevron
        data-e2e="chip-authorization-settings"
      />

      <div
        v-if="isPickingSettings"
        class="q-mt-sm"
      >
        <!-- WITH NOTHING TO PICK FROM, SAY SO. The limit still covers any setting, which is what the
             empty selection means and stays correct — but an expanded picker rendering nothing at
             all leaves a biller who cannot narrow the scope with no idea why. -->
        <div
          v-if="settingOptions.length === 0"
          class="approved-limit-editor__lead"
          data-e2e="text-no-authorization-settings"
        >
          {{ settingsUnavailable
            ? i18n.t('billingAuthorizations.placesOfServiceUnavailable')
            : i18n.t('billingAuthorizations.noPlacesOfService') }}
        </div>

        <!-- No "any setting" chip: deselecting every code IS any setting, and one saying so beside
             a label already saying so would be two controls for one state, able to disagree. -->
        <hr-filter-chip-group
          v-else
          multiple
          :model-value="modelValue.placeOfServiceCodes"
          :options="settingOptions"
          @update:model-value="patch({ placeOfServiceCodes: $event })"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* The one thing a utility class cannot say: a muted role for supporting text. */
.approved-limit-editor__lead {
  color: var(--app-on-surface-variant);
}

/* Once the cap is one line, the fields TOP-align, so an error message hangs under its own field
   instead of re-centering that column and breaking the sentence. The lead-in and the remove control
   carry no message, so they hold a field control's height (Quasar's 56px) to stay centered against
   the boxes rather than riding up to the top with them. */
@media (min-width: $breakpoint-sm-min) {
  .approved-limit-editor__beside-field {
    display: flex;
    align-items: center;
    min-height: 56px;
  }
}
</style>
